import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { RequestService } from '@pisci/requestManager';
import { ExportService } from 'libs/shared/data-exports/src';
import * as _ from 'lodash';

import {
  catchError,
  filter,
  finalize,
  forkJoin,
  map,
  mergeMap,
  of,
  startWith,
  Subject,
  switchMap,
  take,
  tap,
  timer,
} from 'rxjs';
import { ExportsAddModalComponent } from './exports-add-modal/exports-add-modal.component';

interface QueuedReportResponse {
  status: 'queued';
  download_url: string | null;
  data?: {
    download_url?: string | null;
  };
}

@Component({
  selector: 'app-exports',
  templateUrl: './exports.component.html',
  styleUrls: ['./exports.component.scss'],
})
export class ExportsComponent implements OnInit {
  private readonly pollIntervalMs = 3000;
  private readonly maxPollAttempts = 40;

  refresh$ = new Subject();
  exports$;
  ids: number[] = [];
  isProcessing = false;

  constructor(
    private exportService: ExportService,
    private dia: MatDialog,
    private rm: RequestService,
    private snackBar: MatSnackBar,
  ) {
    this.exports$ = this.refresh$.pipe(startWith(1)).pipe(
      mergeMap(() =>
        this.exportService.getAll().pipe(
          map((exports: any) => {
            return _.orderBy(
              _.map(exports, (e) => {
                e.lines = _.orderBy(e.lines, (line: any) => line.id, 'desc');
                return e;
              }),
              (e) => e.id,
              'desc',
            );
          }),
        ),
      ),
    );
  }

  ngOnInit(): void {}

  isSelected(id: any) {
    return this.ids.indexOf(id) !== -1;
  }

  select(id: any, event: any) {
    if (event.target.checked) {
      this.ids.push(id);
    } else {
      const index = this.ids.findIndex((i) => i === id);
      if (index !== -1) {
        this.ids.splice(index, 1);
      }
    }
  }

  clickAdd(id: any) {
    const ref = this.dia.open(ExportsAddModalComponent, { data: { id: id } });
    ref.afterClosed().subscribe(() => {
      this.refresh$.next(0);
    });
  }

  clickReport(type: string, responseType: 'binary' | 'json' = 'binary') {
    if (!this.ids.length) {
      this.snackBar.open('Bitte waehle zuerst mindestens ein Export Set aus.');
      return;
    }

    this.isProcessing = true;
    forkJoin(
      this.ids.map((id) =>
        this.queueReport(id, type, responseType).pipe(
          catchError((error) => {
            const message =
              error instanceof Error
                ? error.message
                : 'Export konnte nicht verarbeitet werden.';
            this.snackBar.open(`Export Set ${id}: ${message}`);
            return of(null);
          }),
        ),
      ),
    )
      .pipe(
        finalize(() => {
          this.isProcessing = false;
        }),
      )
      .subscribe(() => {
        this.snackBar.open('Export-Anfrage abgeschlossen.');
      });
  }

  private queueReport(
    id: number,
    type: string,
    responseType: 'binary' | 'json',
  ) {
    return this.rm
      .post<QueuedReportResponse>('/exports/report', {
        type,
        id,
        filter: 'order',
      })
      .pipe(
        switchMap((response) => {
          if (responseType === 'json') {
            this.snackBar.open(
              `Export Set ${id}: Anfrage wurde in Queue gestellt.`,
            );
            return of(null);
          }

          const downloadUrl =
            response.download_url ??
            response.data?.download_url ??
            this.buildFallbackDownloadUrl(id, type);

          if (!downloadUrl) {
            throw new Error(
              'Kein Download verfuegbar (download_url ist null).',
            );
          }

          return this.pollAndDownload(downloadUrl, id, type);
        }),
      );
  }

  private pollAndDownload(downloadUrl: string, id: number, type: string) {
    let attempts = 0;

    return timer(0, this.pollIntervalMs).pipe(
      mergeMap(() =>
        this.rm.http
          .get(downloadUrl, {
            observe: 'response',
            responseType: 'blob',
          })
          .pipe(
            catchError((error: HttpErrorResponse) => {
              if (error.status === 404) {
                attempts += 1;
                if (attempts >= this.maxPollAttempts) {
                  throw new Error(
                    'Report ist noch nicht bereit. Bitte spaeter erneut versuchen.',
                  );
                }
                return of(null);
              }
              throw error;
            }),
          ),
      ),
      filter((response): response is HttpResponse<Blob> => response !== null),
      take(1),
      tap((response) => this.downloadFile(response, id, type)),
      map(() => null),
    );
  }

  private downloadFile(response: HttpResponse<Blob>, id: number, type: string) {
    if (!response.body) {
      throw new Error('Download war leer.');
    }

    const fileName =
      this.getFilenameFromDisposition(
        response.headers.get('content-disposition'),
      ) ?? `export-${id}-${type}.${this.getDefaultExtension(type)}`;

    const url = window.URL.createObjectURL(response.body);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    anchor.click();
    window.URL.revokeObjectURL(url);
  }

  private getDefaultExtension(type: string) {
    if (type.startsWith('pdf')) {
      return 'pdf';
    }

    if (type.startsWith('orders')) {
      return 'xlsx';
    }

    return 'dat';
  }

  private getFilenameFromDisposition(contentDisposition: string | null) {
    if (!contentDisposition) {
      return null;
    }

    const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
    if (utf8Match?.[1]) {
      return decodeURIComponent(utf8Match[1]);
    }

    const plainMatch = /filename="?([^";]+)"?/i.exec(contentDisposition);
    return plainMatch?.[1] ?? null;
  }

  private buildFallbackDownloadUrl(id: number, type: string) {
    if (type === 'action_orders_to_invoices') {
      return null;
    }

    const searchParams = new URLSearchParams({
      type,
      filter: 'order',
    });

    return `${this.rm.getBaseUrl()}/exports/report/${id}/download?${searchParams.toString()}`;
  }
}
